import React, { useEffect, useRef, useState } from 'react';
import {
	Box,
	Button,
	CircularProgress,
	Collapse,
	Divider,
	IconButton,
	Typography,
} from '@mui/material';
import CustomDataGrid from '../CustomDataGrid/CustomDataGrid';
import SearchBar from '../SearchBar';
import CustomButton from '../CustomButton/CustomButton';
import CustomAvatar from '../CustomAvatar';
import { useAppSelector } from '@src/store/hooks';
import { generateInitialsHelper, getUniqueObjectsByKey } from '@src/helpers/Helpers';
import { ArrowBack, DeleteIcon } from '../Icons';
import CustomModal from '../CustomModal';
import ProductDetails from '../ProductDetails/ProductDetails';
import { ApplicationContext } from '@src/contexts/ApplicationContext';
import { AxiosError, AxiosResponse } from 'axios';
import CustomAlert from '../CustomAlert/CustomAlert';
import EmptyStateComponent from '../EmptyStateComponent/EmptyStateComponent';
import PageHeader from '../PageHeader/PageHeader';
import CustomDataGrid2 from '../CustomDataGrid/CustomDataGrid2';
import Loading from '../Loading/Loading';
import { IsMobileWidth } from '../MediaQueryUtils';
import PageHeaderMobile from '../PageHeader/PageHeaderMobile';
import CategoryCarousel from '../Carousal/CategoryCarousal/CategoryCarousal';
import styled from 'styled-components';
import { Category, Product } from '@src/pages/MarketPlace';
import ProductCustomCard from '../CustomCard/ProductCustomCard';
import { SelectedItemsTextContainer } from '../CommonComponents/Common';
import CustomTextButton from '../CustomButton/CustomTextButton';
import DataNotFound from '../DataItemFound/DataNotFound';
import ProductCustomDetailView from '../CustomDetailView/ProductCustomDetailView';
import ProductCardSkeleton from '../CustomSkeleton/ProductCardSkeleton';
import CategorySliderSkeleton from '../CustomSkeleton/CategorySliderSkeleton';
import ProductCard from '../CustomCard/ProductCard';
import CustomFilter from '../CustomFilter/CustomFilter';
import { Filters, TimeoutHandler } from '@src/interfaces/type';
import ProductList from '../ProductListing/ProductList';

const StyledContainer = styled('div')({
	display: 'flex',
	alignItems: 'center',
	flexWrap: 'wrap',
	'@media (max-width: 768px)': {
		flexDirection: 'column',
		alignItems: 'flex-start',
	},
});
const StyledTypographyBox = styled('div')({
	display: 'flex',
	'@media (max-width: 768px)': {
		marginBottom: '16px',
	},
});

const StyledButtonWrapper = styled('div')({
	'@media (max-width: 478px)': {
		background: 'white',
		display: 'flex',
		position: 'fixed',
		bottom: 0,
		zIndex: '100',
		width: '100%',
		padding: '16px',
		left: 0,
		borderRadius: '6px 6px 0px 0px',
		borderTop: '1.5px solid #F1F1F1',
	},
});

const productDetailsModalStyles = {
	backgroundColor: 'background.paper',
	borderRadius: '4px',
	boxShadow: '24px',
	maxWidth: '600px',
	maxHeight: '444px',
	'@media (max-width: 600px)': {
		width: '90%',
		maxWidth: '90%',
		maxHeight: '645px',
		height: '645px',
	},
};

const SelectedItemsContainer = styled('div')({
	display: 'flex',
	justifyContent: 'center',
	alignItems: 'center',
	gap: '5px',
	fontSize: '16px',
	fontWeight: 750,
	lineHeight: '21.82px',
	transition: 'width 0.3s ease',
});

const StyledMobileContainer = styled.div`
	display: flex;
	flex-direction: column;
	height: 100vh;
	overflow: hidden;
	width: 100%;
	#filter-container {
		display: flex;
		gap: 10px;
		padding-left: 16px;
		padding-right: 16px;
		padding-bottom: 16px;
	}
`;

const ProductContainer = styled.div({
	background: '#F1F1F1',
	paddingTop: '2px',
	overflowY: 'auto',
	flexGrow: 1, // Ensure it takes up remaining space and allows scrolling
});

const StyledBottomBtnContainer = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	width: 100%;
	height: 72px;
	padding: 16px;
	background: var(--chat-header-bg);
	border: 1.5px solid var(--gray-color-light);
	z-index: 10;
	flex-shrink: 0; // Prevents resizing when the content overflows
`;

function SuppliersCatalog({
	setOrderBook,
	productCatalog,
	showProductCatalog,
	waId,
	columns,
	catalogDetails,
	supplierName,
	draft,
	pageContainerStyles,
	selectedIndex,
	setSelectedIndex,
	categories,
	setCategories,
	displayCustomError,
	setAlert,
	getOrderbook,
	showDetails,
}) {
	const [searchInput, setSearchInput] = useState('');
	const [rows, setRows] = useState([]);
	const [btnLoading, setBtnLoading] = useState(false);
	const [isLoading, setIsLoading] = useState(true);
	const [openModal, setOpenModal] = useState(false);
	const [message, setMessage] = useState({ message: '', severity: '' });
	const [selectedProducts, setSelectedProducts] = useState([]);
	const [selectedProductsIDs, setSelectedProductsIDs] = useState([]);
	const [showDoneBtn, setShowDoneBtn] = useState(false);
	const [productData, setProductData] = useState(null);
	const [collapse, setCollapse] = useState(false);
	const [productDetails, setProductDetails] = useState<Product | null>(null);
	const [filters, setFilters] = useState<Filters | null>(null);
	const [activeFilter, setActiveFilter] = useState('');
	const [totalCount, setTotalCount] = useState(0);
	const [page, setPage] = useState(1);
	const [totalPages, setTotalPages] = useState(1);
	const [searching, setSearching] = useState<boolean>(false);
	const [loadingProducts, setLoadingProducts] = useState<boolean>(false);

	const currentUser: any = useAppSelector((state) => state?.currentUser?.value);
	const { apiService }: any = React.useContext(ApplicationContext);
	const isMobilWidth = IsMobileWidth();

	const timeoutHandler = useRef<TimeoutHandler>(null);

	const handleSearch = (value: any) => {
		setSearching(true);
		setSearchInput(value);
		setPage(1);
	};

	const loadMore = () => {
		setLoadingProducts(true);
		//@ts-ignore
		setPage((prevState: number) => prevState + 1);
	};

	const restaurantId: any = useAppSelector((state) => state.restaurant.value.default?.userId);
	const getProducts = (supplierWaId: any, id: string, pageNum?: number) => {
		const limit = 20;
		apiService.getSupplierProducts(
			(response: AxiosResponse) => {
				const data = response?.data?.data;
				const modifiedRowsData = data?.products?.map((item: any) => ({
					...item,
					name: item?.productName,
					categoryId: item?.category?.id,
					category: item?.category?.name,
					categoryObject: { id: item?.category?.id, value: item?.category?.name },
					id: item?.productId?.toString(),
					price: item?.price,
					customerPrice: item?.customerPrice,
					description: item?.description,
					isContracted: item?.isContracted,
					image: item?.image,
					subCategory: item?.subCategory?.name,
					subCategoryId: item?.subCategory?.id,
					machineBrand: item?.machineBrand?.name,
					machineBrandId: item?.machineBrand?.id,
					machineModel: item?.machineModel?.name,
					machineModelId: item?.machineModel?.id,
					groupCategory: item?.groupCategory?.name,
					groupCategoryId: item?.groupCategory?.id,
					brand: item?.brand?.name,
					brandId: item?.brand?.id,
					partComposition: item?.partComposition?.name,
					partCompositionId: item?.partComposition?.id,
					partDesign: item?.partDesign?.name,
					partDesignId: item?.partDesign?.id,
					partDiameter: item?.partDiameter?.name,
					partDiameterId: item?.partDiameter?.id,
					partDimension: item?.partDimension?.name,
					partDimensionId: item?.partDimension?.id,
					partOwnership: item?.partOwnership?.name,
					partOwnershipId: item?.partOwnership?.id,
					partQuality: item?.partQuality?.name,
					partQualityId: item?.partQuality?.id,
					partWeight: item?.partWeight?.name,
					partWeightId: item?.partWeight?.id,
					partPerformance: item?.partPerformance?.name,
					partPerformanceId: item?.partPerformance?.id,
				}));

				const totalCount: number = data?.totalCount;
				const totalPages: number = Math.max(Math.ceil(totalCount / limit), 1);
				setTotalPages(totalPages);
				setTotalCount(totalCount);

				if (pageNum) {
					setPage(1);
				}

				if (page === 1) {
					setRows(modifiedRowsData);
				} else {
					//@ts-ignore
					setRows((prevState: any) => [...prevState, ...modifiedRowsData]);
				}

				setIsLoading(false);
				setLoadingProducts(false);
				setSearching(false);
			},
			(error: AxiosError) => {
				displayCustomError('Something went wrong!');
				setIsLoading(false);
				setLoadingProducts(false);
				setSearching(false);
			},
			supplierWaId,
			id,
			{
				page: page,
				limit: limit,
				category: Number(selectedIndex) === 1000 ? '' : selectedIndex,
				search: searchInput,
				machineModel: filters?.model?.id,
				subCategory: filters?.subCategory?.id,
				machineBrand: filters?.machineBrand?.id,
				dimension: filters?.dimension?.id,
				design: filters?.design?.id,
				weight: filters?.weight?.id,
				composition: filters?.composition?.id,
				brand: filters?.brand?.id,
			}
		);
	};

	const handleCategoryChange = (category: Category) => {
		if (selectedIndex !== category?.category_id) {
			setSelectedIndex(category?.category_id as number);

			if (category?.category_id !== 1000) {
				const rows = rows?.filter((item: any) => item?.categoryId === category?.category_id);
				setRows(rows);
			} else {
				setRows(rows);
			}
		}
	};

	const addToOrderBook = (data: any, supplierWaId: any, id: string) => {
		setBtnLoading(true);
		apiService.addItemsToOrderbook(
			data,
			(response: AxiosResponse) => {
				setAlert({
					message: 'Items Added Successfully.',
					severity: 'success',
				});

				// setShowDoneBtn(true);
				// showProductCatalog(false);
				setBtnLoading(false);
				setSelectedProductsIDs([]);

				//again fetch orderbook
				getOrderbook(waId, id);
				getProducts(waId, restaurantId, 1);
			},
			(error: AxiosError) => {
				displayCustomError('Something went wrong!');
				setBtnLoading(false);
			},
			supplierWaId,
			id
		);
	};

	const onRowsSelectionHandler = (ids: String[]) => {
		const data = ids?.map((id: any) => rows.find((row) => row?.id === id));
		setSelectedProducts(data);
		setSelectedProductsIDs(ids?.map((id) => Number(id)));
	};

	const handleRowClick = (params: any, event: any) => {
		const details: any = [
			{
				name: params?.row?.name,
				image: params?.row?.image || '/prodcut.png',
				images: params?.row?.images?.map((image: any) => {
					return { src: image?.image, alt: 'product-img' };
				}),
				description: params?.row?.description,
				details: [
					`Part Category: ${params?.row?.category}`,
					`Unit: ${params?.row?.unit}`,
					`Price: ${params?.row?.price}`,
				],
			},
		];
		setProductData(details);
		setOpenModal(true);
	};

	const btnView = () => {
		return (
			<CustomButton
				title={
					btnLoading ? (
						<CircularProgress size={16} style={{ color: 'var(--default-text-color)' }} />
					) : (
						'Add To My Order-Book' + ' (' + selectedProductsIDs?.length + ')'
					)
				}
				handleClick={() => {
					const ids = selectedProductsIDs?.map((id) => Number(id));
					addToOrderBook({ productIds: ids }, waId, restaurantId);
				}}
				styles={{
					width: '320px',
					height: '40px',
					transition: 'background 0.3s ease, color 0.3s ease',
				}}
				disabled={!selectedProductsIDs?.length || btnLoading}
			/>
		);
	};

	useEffect(() => {
		getProducts(waId, restaurantId);
	}, [waId, restaurantId, page, filters]);

	useEffect(() => {
		//debounce
		if (timeoutHandler.current) {
			clearTimeout(timeoutHandler.current);
		}
		timeoutHandler.current = setTimeout(() => {
			try {
				getProducts(waId, restaurantId);
			} catch (err) {}
		}, 500);
	}, [searchInput]);

	const viewDetails = (product: Product, e?: any) => {
		if (e?.target?.tagName === 'INPUT' || ['editIcon']?.includes(e?.target?.id)) {
			return;
		}
		setProductDetails(product);
	};

	const handleArrowBack = () => {
		if (productCatalog) {
			showProductCatalog(false);
		} else {
			setOrderBook(false);
		}
	};

	const handleSpecificProductSelect = (id: any) => {
		const ids: string[] = [];

		setRows((prevState: any) => {
			return prevState?.map((item: any) => {
				if ((item?.id !== id && item?.isSelection) || (item?.id === id && !item?.isSelection)) {
					console.log(item?.id);
					ids.push(item?.id);
				}
				if (item?.id === id) {
					return {
						...item,
						isSelection: !item?.isSelection,
					};
				}
				return item;
			});
		});
		setSelectedProductsIDs(ids);
	};

	const handleSelectAll = (products: any) => {
		const ids: string[] =
			selectedProductsIDs?.length === rows?.length ? [] : rows?.map((item: any) => item?.id);
		setRows((prevState: any) => {
			return prevState?.map((item: any) => {
				return {
					...item,
					isSelection: !(selectedProductsIDs?.length === rows?.length),
				};
			});
		});

		setSelectedProductsIDs(ids);
	};

	// useEffect(() => {
	// 	return () => {
	// 		showProductCatalog(false);
	// 	};
	// }, []);

	if (isLoading) {
		// if (isMobilWidth) {
		// 	return (
		// 		<StyledMobileContainer>
		// 			<PageHeaderMobile
		// 				title="Supplier’s Catalog"
		// 				setCollapse={setCollapse}
		// 				collapse={collapse}
		// 				onClick={() => {
		// 					showProductCatalog(false);
		// 				}}
		// 			/>
		// 			<CategorySliderSkeleton />
		// 			<ProductContainer>
		// 				{[1, 2, 3]?.map((item) => {
		// 					return <ProductCardSkeleton key={item} />;
		// 				})}
		// 			</ProductContainer>
		// 		</StyledMobileContainer>
		// 	);
		// }
		return (
			<div style={pageContainerStyles} className="page__container">
				<Loading />
			</div>
		);
	}

	// if (isMobilWidth) {
	// 	if (productDetails) {
	// 		return (
	// 			<ProductCustomDetailView
	// 				productDetails={productDetails}
	// 				handleArrowBack={() => {
	// 					setProductDetails(null);
	// 				}}
	// 			/>
	// 		);
	// 	}
	// 	return (
	// 		<StyledMobileContainer>
	// 			<PageHeaderMobile
	// 				title="Supplier’s Catalog"
	// 				setCollapse={setCollapse}
	// 				collapse={collapse}
	// 				onClick={() => {
	// 					showProductCatalog(false);
	// 				}}
	// 			/>
	// 			{collapse ? (
	// 				<Collapse
	// 					in={collapse}
	// 					style={{
	// 						display: 'flex',
	// 						gap: '15px',
	// 						alignItems: 'center',
	// 						flexShrink: 0,
	// 						padding: '16px',
	// 					}}
	// 				>
	// 					<SearchBar
	// 						style={{
	// 							width: '328px',
	// 							height: '36px',
	// 						}}
	// 						value={searchInput}
	// 						onChange={handleSearch}
	// 						isLoading={false}
	// 						autoFocus
	// 					/>
	// 				</Collapse>
	// 			) : null}
	// 			<CategoryCarousel
	// 				categories={categories}
	// 				handleCategoryChange={handleCategoryChange}
	// 				selectedIndex={selectedIndex}
	// 				setSelectedIndex={setSelectedIndex}
	// 			/>
	// 			<div id="filter-container">
	// 				<div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
	// 					{rows?.length ? (
	// 						<CustomTextButton
	// 							style={{
	// 								fontSize: '13px',
	// 								fontWeight: 700,
	// 								color: '#FF6120',
	// 								textDecoration: 'none',
	// 								maxWidth: '75px',
	// 								marginTop: '4px',
	// 							}}
	// 							title={
	// 								selectedProductsIDs?.length === rows?.length
	// 									? 'Deselect All'
	// 									: 'Select All'
	// 							}
	// 							onClick={() => handleSelectAll(rows)}
	// 						/>
	// 					) : null}
	// 					{rows?.length ? (
	// 						<SelectedItemsTextContainer
	// 							selectedItemIds={selectedProductsIDs}
	// 							filteredItems={rows}
	// 							items
	// 						/>
	// 					) : null}
	// 				</div>
	// 			</div>
	// 			<Divider style={{ width: '100%' }} />
	// 			{rows?.length ? (
	// 				<ProductContainer>
	// 					{rows?.map((item: any) => (
	// 						<ProductCustomCard
	// 							key={item?.id}
	// 							//@ts-ignore
	// 							product={item}
	// 							increaseQuantity={() => {}}
	// 							decreaseQuantity={() => {}}
	// 							recommendedQuantity={() => {}}
	// 							isContainCheckbox
	// 							checkBoxSelection={handleSpecificProductSelect}
	// 							readOnlyCounter
	// 							hideBottom
	// 							viewDetails={viewDetails}
	// 						/>
	// 					))}
	// 				</ProductContainer>
	// 			) : (
	// 				<div style={{ flexGrow: 1 }}>
	// 					<DataNotFound content="No Products Found" height="auto" />
	// 				</div>
	// 			)}
	// 			<StyledBottomBtnContainer>
	// 				<div style={{ display: 'flex', gap: '10px', padding: '10px', flexShrink: 0 }}>
	// 					{btnView()}
	// 				</div>
	// 			</StyledBottomBtnContainer>
	// 		</StyledMobileContainer>
	// 	);
	// }
	return (
		<div style={pageContainerStyles}>
			{message?.message ? <CustomAlert alert={message} setAlert={setMessage} /> : null}
			<PageHeader
				activeIcon={true}
				title="Supplier's Catalog"
				handleArrowBack={handleArrowBack}
				activeHamburgerIcon={false}
			/>
			<Box
				display="flex"
				justifyContent="space-between"
				alignItems="center"
				sx={{
					m: 2,
					textTransform: 'capitalize',
					minHeight: '36px',
					'@media (max-width: 768px)': {
						mb: '0px',
						alignItems: 'flex-start',
					},
				}}
			>
				<Box
					sx={{
						width: '315px',
						'@media (max-width: 550px)': {
							width: '100%',
						},
					}}
				>
					<SearchBar isLoading={false} value={searchInput} onChange={(val) => handleSearch(val)} />
				</Box>
				<StyledButtonWrapper>
					{/* {showDoneBtn ? (
						<CustomButton
							title="Done"
							heightVariant="medium"
							color="secondary"
							handleClick={() => {
								handleArrowBack();
							}}
							styles={{ marginRight: '15px' }}
						/>
					) : null} */}
					{rows?.length === 0 ? null : (
						<CustomButton
							styles={{
								width: '209.5px',
								'@media (max-width: 478px)': {
									width: '100%',
								},
							}}
							title={
								btnLoading ? (
									<CircularProgress size={16} style={{ color: 'var(--default-text-color)' }} />
								) : (
									'Add To My Order-Book' + ' (' + selectedProductsIDs?.length + ')'
								)
							}
							heightVariant="medium"
							disabled={!selectedProductsIDs?.length || btnLoading}
							color="primary"
							handleClick={() => {
								const ids = selectedProductsIDs?.map((id) => Number(id));
								addToOrderBook({ productIds: ids }, waId, restaurantId);
							}}
						/>
					)}
				</StyledButtonWrapper>
			</Box>
			<Box
				display="flex"
				justifyContent="flex-end"
				alignItems="center"
				sx={{
					px: 2,
					'@media (max-width: 550px)': {
						flexDirection: 'column',
						alignItems: 'flex-end',
						minHeight: isMobilWidth ? (selectedProducts?.length ? 'unset' : '0px') : '75px',
					},
				}}
			>
				{selectedProducts?.length >= 1 && productCatalog ? (
					<Box>{selectedProducts?.length} items selected</Box>
				) : null}
			</Box>
			<Box
				sx={
					isMobilWidth
						? {
								height: '74vh',
								// paddingBottom: '200px',
								overflow: 'auto',
						  }
						: {
								overflow: 'auto',
						  }
				}
			>
				<CustomFilter
					filters={filters}
					setFilters={setFilters}
					activeFilter={activeFilter}
					setActiveFilter={setActiveFilter}
					products={rows}
					setFilteredProducts={setRows}
					setLoading={setSearching}
					filterType="server"
					setPage={setPage}
				/>
				<Divider />
				{isLoading ? (
					<Box
						sx={{
							width: '100%',
							height: '100%',
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'center',
						}}
					>
						<div className="chat__body__loadingMore__wrapper">
							<CircularProgress size={28} />
						</div>
					</Box>
				) : rows?.length !== 0 && !draft ? (
					rows?.length ? (
						<ProductList
							//@ts-ignore
							products={rows}
							searching={searching}
							loadingProducts={loadingProducts}
							loadMore={loadMore}
							handleSpecificProductSelect={handleSpecificProductSelect}
							showDetails={showDetails}
							style={{ overflow: 'auto', height: 'calc(100vh - 280px)' }}
							totalCount={totalCount}
						/>
					) : (
						<div
							style={{
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'center',
								height: 'calc(100vh - 340px)',
							}}
						>
							No Products Found
						</div>
					)
				) : (
					<EmptyStateComponent
						showProductCatalog={showProductCatalog}
						isSupplierCatalog={true}
						draft={draft}
					/>
				)}
			</Box>
			{rows?.length !== 0 ? (
				<SelectedItemsContainer style={{ justifyContent: 'flex-end', marginRight: '16px' }}>
					<Typography>Total Items: {totalCount}</Typography>
				</SelectedItemsContainer>
			) : null}
			<CustomModal
				open={openModal}
				setOpen={setOpenModal}
				header={false}
				style={productDetailsModalStyles}
			>
				<ProductDetails data={productData} setOpenModal={setOpenModal} />
			</CustomModal>
		</div>
	);
}

export default SuppliersCatalog;
